<template>
    <v-card elevation="0" dark color="transparent">
        <div class="upload-file-shell">
            <div class="upload-file-shell-inner">
                <div class="upload-file-hero">
                    <div class="upload-kicker">
                        <v-icon small class="upload-kicker-icon">
                            {{ mdiCloudUploadOutline }}
                        </v-icon>
                        <span>BULK UPLOAD</span>
                    </div>
                    <h1 class="upload-title">Upload Serial File</h1>
                    <p class="upload-description">
                        Upload an excel sheet with serials to register a bulk amount at once. Please download the format
                        example to see the accepted format.
                    </p>
                    <div class="upload-hero-accent"></div>
                </div>

                <v-row class="upload-grid" align="stretch">
                    <v-col cols="12" lg="12" class="upload-form-panel">
                        <v-card class="upload-form-card" elevation="0" dark>
                            <v-progress-linear
                                v-if="uploadLoading"
                                indeterminate
                                color="#db5120"
                                height="3"
                                class="mb-4"
                            />

                            <v-row dense>
                                <v-col v-if="userIsOrgAdmin" cols="12" md="6">
                                    <v-select
                                        v-model="uploadSerialToOrg"
                                        dark
                                        outlined
                                        dense
                                        hide-details
                                        return-object
                                        item-text="organizationName"
                                        label="Select Organization"
                                        clearable
                                        :items="orgList"
                                    >
                                        <template v-slot:prepend-inner>
                                            <v-icon class="mt-1 mr-1" small>
                                                {{ mdiAccountSupervisorCircle }}
                                            </v-icon>
                                        </template>
                                    </v-select>
                                </v-col>

                                <v-col cols="12" :md="userIsOrgAdmin ? 12 : 12">
                                    <div class="upload-dropzone mt-3">
                                        <div class="upload-dropzone-copy">
                                            <div class="upload-dropzone-title"> Choose a serial file </div>
                                            <div class="upload-dropzone-subtitle">
                                                All serials listed in your file will automatically be registered once
                                                uploaded.
                                            </div>
                                            <div
                                                v-if="userIsOrgAdmin"
                                                class="upload-dropzone-subtitle"
                                                style="text-decoration: underline"
                                                ><strong
                                                    >If a serial(s) in your uploaded file are already registered in
                                                    different organization branches, they will be removed from those
                                                    branches and will be registered into the new selected
                                                    organization.</strong
                                                ></div
                                            >
                                        </div>
                                        <v-row>
                                            <v-col cols="12" md="6">
                                                <v-btn
                                                    class="upload-primary-btn"
                                                    color="primary"
                                                    block
                                                    outlined
                                                    dark
                                                    :loading="uploadLoading"
                                                    :disabled="uploadSerialDisabled"
                                                    @click="$refs.serialUpload.click()"
                                                >
                                                    <v-icon left small>{{ mdiCloudUploadOutline }}</v-icon>
                                                    Upload Serials
                                                </v-btn>
                                                <input
                                                    v-show="false"
                                                    ref="serialUpload"
                                                    type="file"
                                                    accept=".csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsb"
                                                    @change="
                                                        (e) => {
                                                            $emit(
                                                                'upload-file',
                                                                e.target.files[0],
                                                                uploadSerialToOrg.orgId
                                                            )
                                                            $refs.serialUpload.value = ``
                                                        }
                                                    "
                                                />
                                            </v-col>
                                            <v-col cols="12" md="6">
                                                <v-btn
                                                    class="upload-primary-btn"
                                                    outlined
                                                    tile
                                                    block
                                                    :disabled="uploadLoading"
                                                    @click="$emit('download-format-example')"
                                                >
                                                    <v-icon left small>{{ mdiFileDownloadOutline }}</v-icon>
                                                    Download format example
                                                </v-btn>
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-col>
                            </v-row>

                            <v-divider class="upload-divider mt-10" />

                            <div class="upload-status d-flex flex-column">
                                <div class="upload-status-label">Selected file</div>
                                <div class="upload-status-value">
                                    {{ uploadFileName || 'No file selected yet' }}
                                </div>
                                <div class="upload-status-label">Uploaded serials</div>
                                <v-chip
                                    v-if="!uploadLoading && uploadedSns && uploadedSns.length"
                                    label
                                    class="ma-2 flex-grow-0 flex-shrink-0"
                                    color="success"
                                    text-color="white"
                                    style="align-self: flex-start; width: auto"
                                >
                                    {{ uploadedSns.length }} serials successfully uploaded and registered.
                                </v-chip>
                                <v-data-table
                                    style="background-color: transparent"
                                    dark
                                    disable-sort
                                    no-data-text="No serial(s) uploaded"
                                    :headers="uploadHeaders"
                                    :items="uploadedSns"
                                >
                                </v-data-table>
                            </div>
                        </v-card>
                    </v-col>
                </v-row>
            </div>
        </div>
    </v-card>
</template>

<script>
import {
    mdiCloudUploadOutline,
    mdiFileExcelOutline,
    mdiCheckboxMarkedCircleOutline,
    mdiAccountSupervisorCircle,
    mdiFileDownloadOutline
} from '@mdi/js'

export default {
    name: 'UploadFileTab',
    props: {
        userIsOrgAdmin: {
            type: Boolean,
            required: true
        },
        orgList: {
            type: Array,
            default: () => []
        },
        uploadLoading: {
            type: Boolean,
            default: false
        },
        uploadFileName: {
            type: String,
            default: ''
        },
        uploadedSns: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            mdiCloudUploadOutline,
            mdiFileExcelOutline,
            mdiCheckboxMarkedCircleOutline,
            mdiAccountSupervisorCircle,
            mdiFileDownloadOutline,
            uploadSerialToOrg: '',
            uploadHeaders: [
                {
                    text: 'Serial #',
                    value: 'sn'
                },
                {
                    text: 'Asset Tag',
                    value: 'assetTag'
                },
                {
                    text: 'Location',
                    value: 'location'
                }
            ]
        }
    },
    computed: {
        uploadSerialDisabled() {
            if (this.uploadLoading) {
                return true
            }

            if (this.userIsOrgAdmin && (!this.uploadSerialToOrg || !this.uploadSerialToOrg.orgId)) {
                return true
            }

            return false
        }
    }
}
</script>

<style scoped>
.upload-file-shell {
    position: relative;
    overflow: hidden;
    padding: 8px 0 6px;
}

.upload-file-shell-inner {
    position: relative;
    z-index: 1;
}

.upload-file-shell::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top right, rgba(219, 81, 32, 0.22), transparent 34%),
        radial-gradient(circle at bottom left, rgba(255, 255, 255, 0.08), transparent 28%);
    pointer-events: none;
}

.upload-file-hero {
    max-width: 860px;
    margin-bottom: 24px;
}

.upload-kicker {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding: 6px 12px;
    border: 1px solid rgba(219, 81, 32, 0.3);
    border-radius: 999px;
    background: rgba(219, 81, 32, 0.12);
    color: #db5120;
    font-size: 12px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.upload-kicker-icon {
    color: #db5120 !important;
}

.upload-title {
    margin: 0;
    font-size: clamp(2rem, 4vw, 3.25rem);
    line-height: 1.02;
    letter-spacing: -0.08rem;
}

.upload-description {
    margin: 14px 0 0;
    max-width: 62ch;
    font-size: 1rem;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.76);
}

.upload-hero-accent {
    margin-top: 14px;
    width: min(420px, 92%);
    height: 2px;
    border-radius: 999px;
    background: linear-gradient(90deg, #db5120 0%, rgba(219, 81, 32, 0.22) 100%);
}

.upload-hero-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 18px;
}

.upload-hero-chip {
    border-color: rgba(219, 81, 32, 0.3) !important;
    background: rgba(219, 81, 32, 0.08) !important;
    color: rgba(255, 255, 255, 0.92) !important;
}

.upload-hero-chip .v-icon {
    color: #db5120 !important;
}

.upload-grid {
    position: relative;
    z-index: 1;
}

.upload-form-panel {
    display: flex;
}

.upload-form-card {
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 20px !important;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03)) !important;
    box-shadow: 0 18px 48px rgba(0, 0, 0, 0.24);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.upload-dropzone {
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-height: 100%;
    padding: 18px;
    border-radius: 18px;
    background: linear-gradient(180deg, rgba(219, 81, 32, 0.16), rgba(255, 255, 255, 0.03));
    border: 1px dashed rgba(219, 81, 32, 0.34);
}

.upload-dropzone-copy {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.upload-dropzone-title {
    font-size: 1.15rem;
    font-weight: 700;
    letter-spacing: -0.02rem;
}

.upload-dropzone-subtitle {
    color: rgba(255, 255, 255, 0.72);
    line-height: 1.6;
}

.upload-primary-btn {
    align-self: flex-start;
    min-width: 180px;
}

.upload-divider {
    margin: 20px 0 !important;
    border-color: rgba(255, 255, 255, 0.14) !important;
}

.upload-status {
    gap: 8px;
}

.upload-status-label {
    color: rgba(255, 255, 255, 0.55);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
}

.upload-status-value {
    min-height: 28px;
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.9);
    word-break: break-word;
}

@media (max-width: 959px) {
    .upload-form-card {
        padding: 20px;
    }

    .upload-primary-btn {
        width: 100%;
        min-width: 0;
        align-self: stretch;
    }
}

@media (max-width: 600px) {
    .upload-file-shell {
        padding-top: 0;
    }

    .upload-file-shell::before,
    .upload-file-shell-inner::before {
        display: none;
    }

    .upload-title {
        font-size: clamp(1.8rem, 8vw, 2.4rem);
    }

    .upload-description {
        font-size: 0.95rem;
    }
}
</style>
